import {
  hydrateInvoiceDetail,
  markHydrated,
  purchaseOrderUpdateRowSelection,
  selectActivePage,
  selectHydrationState,
  updateInvoiceDetail,
} from "../../activeScreenSlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import InvoiceBar from "../../components/InvoiceBar/InvoiceBar"
import InvoiceDetailNav from "../../components/InvoiceDetailNav/InvoiceDetailNav"
import InvoiceInformationSidebar from "../../components/InvoiceInformationSidebar/InvoiceInformationSidebar"
import { useInvoiceDetails } from "../../hooks"
import {
  InvoiceBarWrapper,
  InvoiceDetailPageLayout,
  InvoiceNavWrapper,
  SidebarWrapper,
  WidgetBoxWrapper,
} from "./InvoiceDetail.styles"
// @ts-ignore
import { useEffect, useState } from "react"
import InvoiceAttachments from "../../InvoiceAttachments/InvoiceAttachments"
import { InvoiceType, UiEditContainer, UserRole } from "../../api/generated"
import InvoiceHistory from "../../components/InvoiceHistory/InvoiceHistory"
import PurchaseOrderLinks from "../../components/PurchaseOrderLinks/PurchaseOrderLinks"
import { OPCOS_THAT_BYPASS_TAX_VALIDATION } from "../../constants"
import useShowError from "../../hooks/useShowError"
import { getSupplierSearch } from "../../service/Supplier"
import NonPoRequesterTab from "../../components/NonPoRequesterTab/NonPoRequesterTab"

export interface Props {
  currentUserId?: number
  currentUserRole?: UserRole
}

function convertToDdMmYyyy(date = "") {
  if (!date) {
    return date
  }
  const isAlreadyCorrectFormat = /^\d{2}-\d{2}-\d{4}$/.test(date)
  if (isAlreadyCorrectFormat) {
    return date
  }

  // 2020-05-20 -> 20-05-2020
  return date.split("-").reverse().join("-")
}

export default function InvoiceDetail(props: Props) {
  const showError = useShowError()
  const invoiceId = useAppSelector(selectActivePage).selectedInvoice
  const [activeTab, setActiveTab] = useState(1)

  const dispatch = useAppDispatch()
  const initialValuesQuery = useInvoiceDetails(invoiceId!)

  const hydrated = useAppSelector(selectHydrationState)

  useEffect(() => {
    if (initialValuesQuery.data) {
      const supplierOptions =
        initialValuesQuery.data.latest_revision &&
        initialValuesQuery.data.latest_revision.linked_supplier
          ? [
              {
                id: initialValuesQuery.data.latest_revision?.linked_supplier
                  ?.supplier_site_id,
                name: initialValuesQuery.data.latest_revision?.linked_supplier
                  ?.site_code,
              },
            ]
          : []

      const initialRowSeleciton =
        initialValuesQuery.data.latest_revision &&
        initialValuesQuery.data.latest_revision.invoice_lines_items
          ? initialValuesQuery.data.latest_revision?.invoice_lines_items?.map(
              (row) => row.temp_line_identifier,
            )
          : []

      // Dispatch an action to update Redux state with the fetched data
      dispatch(
        hydrateInvoiceDetail({
          lineItems:
            initialValuesQuery.data.latest_revision?.invoice_lines_items?.map(
              (line) =>
                ({
                  ...line,
                  non_editable_po_fields: line.non_editable_po_fields || {},
                } as UiEditContainer),
            ) || [],
          rowSelectionModel: initialRowSeleciton,
          opcoSlug: initialValuesQuery.data.opco.slug,
          purchaseOrderRowSelectionCount: 0,
          purchaseOrderRowSelectionSum: 0,
          purchaseOrderRowSelectionMismatch: 0,
          assignedUser: initialValuesQuery.data.assigned_user?.email || "",
          invoiceId: initialValuesQuery.data.id,
          externalInvoiceId:
            initialValuesQuery.data.latest_revision?.external_invoice_id || "",
          receiverNameInvoice:
            initialValuesQuery.data.latest_revision?.receiver_name || "",
          supplierSiteId:
            initialValuesQuery.data.latest_revision?.linked_supplier
              ?.supplier_site_id,
          supplierSiteOptions: supplierOptions,
          poNumber: "",
          currency: initialValuesQuery.data.latest_revision?.currency || "",
          grossAmount:
            initialValuesQuery.data.latest_revision?.total_amount || undefined,
          taxAmount:
            initialValuesQuery.data.latest_revision?.total_tax_amount !==
            undefined
              ? initialValuesQuery.data.latest_revision?.total_tax_amount
              : undefined,
          totalLineAmount:
            initialValuesQuery.data.latest_revision?.net_amount || undefined,
          businessUnit:
            initialValuesQuery.data.supplier_site?.business_unit || "",
          receiverNamePO:
            initialValuesQuery.data.purchase_order?.bill_to_legal_entity_name ||
            "",
          invoiceDate: convertToDdMmYyyy(
            initialValuesQuery.data.latest_revision?.invoice_date,
          ),
          glDate: convertToDdMmYyyy(initialValuesQuery.data.gl_date),
          invoiceType: initialValuesQuery.data.latest_revision
            ?.invoice_type as InvoiceType,
          paymentTerms:
            initialValuesQuery.data.purchase_order?.payment_terms || "",
          paymentMethod:
            initialValuesQuery.data.supplier_site?.payment_method || "",
          payGroup: initialValuesQuery.data.supplier_site?.pay_group || "",
          bankDetailsString:
            initialValuesQuery.data.supplier_bank_details_string || "",
          invoice_user_flag:
            initialValuesQuery.data.invoice_user_flag || undefined,
          note: initialValuesQuery.data.note_text || "",
          validationErrors:
            initialValuesQuery.data.current_failed_exceptions || [],
          supplierName: initialValuesQuery.data.supplier?.supplier_name || "",
          supplierNumber:
            initialValuesQuery.data.supplier?.supplier_number || "",
          supplierId: initialValuesQuery.data.supplier?.id || undefined,
          invoiceSupplierName:
            initialValuesQuery.data.latest_revision?.supplier_name || "",
          showInvoiceInline: initialValuesQuery.data.render_inline_flag,
          supplierBankDetailsId:
            initialValuesQuery.data.latest_revision?.supplier_bank_details_id ||
            undefined,
          supplierIban: initialValuesQuery.data.latest_revision?.supplier_iban,
          supplierBicSwift:
            initialValuesQuery.data.latest_revision?.supplier_bic_swift,
          supplierBankAccountNumber:
            initialValuesQuery.data.latest_revision
              ?.supplier_bank_account_number,
          supplierSortCode:
            initialValuesQuery.data.latest_revision?.supplier_sort_code,
          qrIban: initialValuesQuery.data.latest_revision?.qr_iban,
          qrIbanReference:
            initialValuesQuery.data.latest_revision?.qr_iban_reference,
          isQrr: initialValuesQuery.data.latest_revision?.is_qrr,
          spotRates:
            initialValuesQuery.data.latest_revision?.spot_rates || undefined,
          nonPoRequester:
            initialValuesQuery.data.latest_revision?.non_po_requester || "",
          nonPoAttribute:
            initialValuesQuery.data.supplier?.non_po_attribute || "",
          hydrated: false,
          isNonPoValidationRuleEnabled: initialValuesQuery.data?.non_po_validation_rule
        }),
      )
      dispatch(purchaseOrderUpdateRowSelection(initialRowSeleciton))
      if (
        initialValuesQuery.data.supplier?.supplier_number !== undefined &&
        initialValuesQuery.data.supplier.supplier_number.length >= 1
      ) {
        getSupplierSearch(
          "supplierNumber",
          initialValuesQuery.data.supplier.supplier_number,
          initialValuesQuery.data.opco.slug,
        )
          .then((res) => {
            const options = res.map((opt) => {
              return {
                supplierName: opt.supplier_name,
                supplierNumber: opt.supplier_number,
                supplierId: opt.id,
                supplierSites: opt.supplier_sites.map((site) => {
                  return {
                    id: site.id,
                    name: site.site_code,
                  }
                }),
              }
            })
            const matchingOptions = options.filter(
              (option) =>
                option.supplierNumber ===
                initialValuesQuery.data.supplier!.supplier_number,
            )
            const option = matchingOptions[0]
            dispatch(
              updateInvoiceDetail({
                key: "supplierSiteOptions",
                value: option.supplierSites,
              }),
            )
            dispatch(markHydrated())
          })
          .catch((error) => {
            showError(
              "An error occurred while searching for supplier number: " +
                error.name +
                " " +
                error.message,
            )
          })
      } else {
        dispatch(markHydrated())
      }
    }
  }, [dispatch, initialValuesQuery.data])

  if (initialValuesQuery.isLoading) {
    return null
  }

  if (initialValuesQuery.isError) {
    return <p>Error</p>
  }

  return (
    <InvoiceDetailPageLayout>
      <InvoiceBarWrapper>
        <InvoiceBar
          enableSaveAndSubmit={
            !initialValuesQuery.data?.assigned_user ||
            props.currentUserId === initialValuesQuery.data?.assigned_user?.id
          }
        />
      </InvoiceBarWrapper>
      <SidebarWrapper>
        {initialValuesQuery.data && hydrated && (
          <InvoiceInformationSidebar
            bypassTaxValidation={OPCOS_THAT_BYPASS_TAX_VALIDATION.includes(
              initialValuesQuery.data.opco.slug,
            )}
          />
        )}
      </SidebarWrapper>
      <InvoiceNavWrapper>
        <InvoiceDetailNav
          handleChange={(event, value) => setActiveTab(value)}
          value={activeTab}
        />
      </InvoiceNavWrapper>
      <WidgetBoxWrapper>
        {activeTab === 1 && !!initialValuesQuery.data && (
          <InvoiceAttachments
            emailId={initialValuesQuery.data.email.id}
            emailBody={initialValuesQuery.data.email.body}
            emailSender={initialValuesQuery.data.email.sender}
            emailSubject={initialValuesQuery.data.email.subject}
            invoiceUrl={initialValuesQuery.data.gcs_location}
            renderInvoiceInline={initialValuesQuery.data.render_inline_flag}
          />
        )}
        {activeTab === 2 && <PurchaseOrderLinks />}
        {activeTab === 3 && (
          <InvoiceHistory invoiceId={initialValuesQuery.data!.id} />
        )}
        {activeTab === 4 && <NonPoRequesterTab />}
      </WidgetBoxWrapper>
    </InvoiceDetailPageLayout>
  )
}
